import { Injectable } from '@angular/core';
import { WS } from '@services/util/WebSocket.service';
import { filter, map, Observable } from 'rxjs';
import { ChatManualStatus, ChatRealtimeEvent } from '../models/chat.models';

/**
 * Chat's view of the app's single WebSocket.
 *
 * Remmi already keeps one authenticated socket open for the whole session (notifications, email
 * sync, sale-board activity), complete with heartbeat and exponential-backoff reconnect. Chat
 * rides on it under `type: 'chat'` rather than opening a second connection — a second socket
 * would double the presence bookkeeping the server does per user and give us a second thing to
 * reconnect.
 *
 * This service is a thin translation layer: typed events out, the two client frames in. All
 * state lives in {@link ChatStore}.
 */
@Injectable({ providedIn: 'root' })
export class ChatRealtimeService {
  constructor(private readonly ws: WS) {}

  /** Server → client chat frames, already narrowed to `{ action, data }`. */
  readonly events$: Observable<ChatRealtimeEvent> = this.ws.getChatEvents().pipe(
    filter((frame: any) => !!frame?.action),
    map((frame: any) => ({ action: frame.action, data: frame.data }) as ChatRealtimeEvent)
  );

  /** Fires on every (re)connect so the store can refill anything missed while the socket was down. */
  readonly reconnected$: Observable<unknown> = this.ws.reconnectChat.asObservable();

  /** Manual Online ⇄ Do Not Disturb. Also sent over REST so it survives a socket that is down. */
  publishStatus(status: ChatManualStatus): void {
    this.ws.sendChat('presence-update', { status });
  }

  /**
   * Fire-and-forget typing signal — never persisted, never acknowledged.
   *
   * `name` is passed even though §7.2 does not ask for it: §7.1 requires the server to put a name
   * on the frame it relays, and handing it the one we already hold means that can be a straight
   * copy rather than a user lookup on the hot path of every keystroke in the office. The server is
   * free to ignore it and use its own.
   */
  publishTyping(conversationId: string, isTyping: boolean, name = ''): void {
    this.ws.sendChat('typing', { conversation_id: conversationId, is_typing: isTyping, name });
  }

  /**
   * Whether the socket is actually open — the composer stays enabled either way.
   *
   * Not `!socket$.closed`: that is the RxJS Subject's flag and stays false after the underlying
   * socket has errored or closed, so chat believed it was connected while the server held nothing.
   */
  get isConnected(): boolean {
    return this.ws.isOpen;
  }

  /** Re-establish the socket if we are logged in and have none. Cheap; safe to call often. */
  ensureConnected(): void {
    this.ws.ensureConnected();
  }

  /** Drop the socket and open a fresh one — for a socket that looks open here but not to the server. */
  forceReconnect(): void {
    this.ws.forceReconnect();
  }
}
